package fr.manyverse.app.infrastructure.filestorage

import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.khronos.webgl.get
import org.khronos.webgl.set
import org.w3c.dom.events.Event
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

actual fun createLocalFileStorage(): LocalFileStorage = IndexedDbFileStorage()

/**
 * JavaScript implementation of LocalFileStorage using IndexedDB.
 * IndexedDB is used instead of localStorage because localStorage has a 5MB limit
 * which is insufficient for audio files.
 */
class IndexedDbFileStorage : LocalFileStorage {

    companion object {
        private const val DB_NAME = "manyverse_files"
        private const val STORE_NAME = "files"
        private const val DB_VERSION = 1
    }

    override suspend fun saveFile(relativePath: String, bytes: ByteArray): Result<String> =
        runCatching {
            val db = openDatabase()
            val transaction = db.transaction(arrayOf(STORE_NAME), "readwrite")
            val store = transaction.objectStore(STORE_NAME)

            val int8Array = Int8Array(bytes.size)
            for (i in bytes.indices) {
                int8Array[i] = bytes[i]
            }

            suspendCancellableCoroutine { continuation ->
                val request = store.put(int8Array.buffer, relativePath)
                request.onsuccess = { _: Event -> continuation.resume(relativePath) }
                request.onerror =
                    { _: Event -> continuation.resumeWithException(Exception("Failed to save file: $relativePath")) }
            }
        }

    override suspend fun readFile(relativePath: String): Result<ByteArray> =
        runCatching {
            val db = openDatabase()
            val transaction = db.transaction(arrayOf(STORE_NAME), "readonly")
            val store = transaction.objectStore(STORE_NAME)

            suspendCancellableCoroutine { continuation ->
                val request = store.get(relativePath)
                request.onsuccess = { _: Event ->
                    val result = request.result
                    if (result != null) {
                        val arrayBuffer = result.unsafeCast<ArrayBuffer>()
                        val int8Array = Int8Array(arrayBuffer)
                        val bytes = ByteArray(int8Array.length)
                        for (i in 0 until int8Array.length) {
                            bytes[i] = int8Array[i]
                        }
                        continuation.resume(bytes)
                    } else {
                        continuation.resumeWithException(Exception("File not found: $relativePath"))
                    }
                }
                request.onerror =
                    { _: Event -> continuation.resumeWithException(Exception("Failed to read file: $relativePath")) }
            }
        }

    override suspend fun deleteFile(relativePath: String): Result<Unit> =
        runCatching {
            val db = openDatabase()
            val transaction = db.transaction(arrayOf(STORE_NAME), "readwrite")
            val store = transaction.objectStore(STORE_NAME)

            suspendCancellableCoroutine { continuation ->
                val request = store.delete(relativePath)
                request.onsuccess = { _: Event -> continuation.resume(Unit) }
                request.onerror =
                    { _: Event -> continuation.resumeWithException(Exception("Failed to delete file: $relativePath")) }
            }
        }

    override suspend fun fileExists(relativePath: String): Boolean {
        return runCatching {
            val db = openDatabase()
            val transaction = db.transaction(arrayOf(STORE_NAME), "readonly")
            val store = transaction.objectStore(STORE_NAME)

            suspendCancellableCoroutine { continuation ->
                val request = store.get(relativePath)
                request.onsuccess = { _: Event -> continuation.resume(request.result != null) }
                request.onerror = { _: Event -> continuation.resume(false) }
            }
        }.getOrDefault(false)
    }

    override suspend fun deleteDirectory(relativePath: String): Result<Unit> =
        runCatching {
            val db = openDatabase()
            val transaction = db.transaction(arrayOf(STORE_NAME), "readwrite")
            val store = transaction.objectStore(STORE_NAME)

            // Get all keys and delete those that start with the directory path
            suspendCancellableCoroutine { continuation ->
                val request = store.openCursor()
                request.onsuccess = { _: Event ->
                    val cursor = request.result
                    if (cursor != null) {
                        val key = cursor.key.toString()
                        if (key.startsWith(relativePath)) {
                            cursor.delete()
                        }
                        cursor.`continue`()
                    } else {
                        continuation.resume(Unit)
                    }
                }
                request.onerror =
                    { _: Event -> continuation.resumeWithException(Exception("Failed to delete directory: $relativePath")) }
            }
        }

    private suspend fun openDatabase(): dynamic {
        return suspendCancellableCoroutine { continuation ->
            val request = js("indexedDB.open")(DB_NAME, DB_VERSION)

            request.onupgradeneeded = { event: Event ->
                val db = request.result
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    db.createObjectStore(STORE_NAME)
                }
            }

            request.onsuccess = { _: Event ->
                continuation.resume(request.result)
            }

            request.onerror = { _: Event ->
                continuation.resumeWithException(Exception("Failed to open IndexedDB"))
            }
        }
    }
}
